import { useCallback, useMemo, useState } from 'react';

import type { AutocompleteInputChangeReason } from '@mui/material/Autocomplete';
import { createFilterOptions } from '@mui/material/Autocomplete';

interface HasId {
  id?: UUID | null;
}

interface HasStringField {
  [key: string]: string | undefined | null;
}

interface UseUniqueFilterOptionsParams<T extends HasId> {
  compareFn: (a: T, b: T) => number;
  items: T[];
}

/**
 * Shared sizing for every filter autocomplete. The width suits the grid's
 * filter panel, but these inputs also render in narrower hosts such as the AI
 * report approval card, so it has to be a ceiling the input can shrink below
 * rather than a floor that overflows the container.
 */
export const filterAutocompleteStyle = { maxWidth: '100%', width: 500 };

/**
 * Keeps the Autocomplete search text when the user selects an option. MUI can
 * fire `onInputChange` with a selection reason and an empty string, which would
 * force users to retype when picking multiple nearby matches.
 */
export function usePreserveAutocompleteSearchInput() {
  const [inputValue, setInputValue] = useState('');

  const onInputChange = useCallback(
    (_event: unknown, newInputValue: string, reason?: AutocompleteInputChangeReason) => {
      if (reason === 'reset' || reason === 'selectOption' || reason === 'removeOption') return;
      setInputValue(newInputValue);
    },
    []
  );

  return { inputValue, onInputChange };
}

export function createAutocompleteFilterOptions<T extends HasStringField>(stringifyField: keyof T) {
  return createFilterOptions<T>({
    stringify: option => option[stringifyField] ?? '',
    trim: true,
  });
}

export function useUniqueFilterOptions<T extends HasId>({
  compareFn,
  items,
}: UseUniqueFilterOptionsParams<T>): T[] {
  function deduplicateById<T extends HasId>(items: T[]): T[] {
    const uniqueItems = new Map<UUID, T>();
    for (const item of items) {
      if (item.id && !uniqueItems.has(item.id)) {
        uniqueItems.set(item.id, item);
      }
    }
    return uniqueItems.values().toArray();
  }

  return useMemo(() => {
    return deduplicateById(items).sort(compareFn);
  }, [items, compareFn]);
}
